/**
 * "Your last inspection" (owner, 2026-10-11: "Yes, both"): a lasting page for a business whose location is linked to its health
 * department record (Location.officialPlaceKey), shown only where that source's display is approved (displayApprovedOf). Every
 * item the latest inspection cited, critical first, in the department's words with its code; for each, the daily check that now
 * keeps it right here, or a one-tap "Add it", or "Nothing in the app for this one yet". Items cited at more than one inspection
 * we hold are counted ("Cited at 3 of your last 4 inspections"). A clean latest inspection says so and lists what places like
 * this one are cited for most (commonAhead), with their coverage. The same view feeds the binder's "Inspection follow-up" page,
 * Today's one quiet row and the prep hand-off preview's block. Pure: the API, the web and the tests share it.
 *
 * Coverage (INSPECTION_COVER): FDA Form 3-A item → what in the app keeps it right every day:
 *   base     a built-in check of the place's plan (schedule.ts BASE_CHECKS, at its permit, logging level and kitchen: levelPlan),
 *   builtin  a flow every business runs (the health check-in before each shift),
 *   titles   a checklist item (TaskDefinition) the place has on, by title: the starter checklist's (tasks.ts) or the one "Add it" makes.
 * "Add it" makes ONE checklist item for this location (`add`), the starter checklist's own row where one exists. The cadences
 * of the others are ASSUMED (per shift for a log a shift produces, weekly for a walk-around check), labeled here, not sourced.
 *
 * Rules (packages/core/test/lastinspection.test.ts, each with a control):
 * | I1 Facts only       | every cited line is the department's words and code; ours only say which check covers it       |
 * | I2 Never a grade    | no score, grade or ranking; none of roomwalk.ts JUDGEMENT_WORDS in anything we write            |
 * | I3 Never invent     | an item with no mapping says "Nothing in the app for this one yet" and nothing more of ours     |
 * | I4 Covered is real  | "Covered" only when the check is in this place's plan or an active checklist item here          |
 * | I5 Repeats counted  | only inspections whose list we hold; the same code with or without the critical mark is one     |
 */
import { AHEAD, AHEAD_DEFAULT, AHEAD_MAX } from "./ahead.ts";
import type { BinderSection } from "./binder.ts";
import { citedOf, isClean } from "./isthisyou.ts";
import type { TaskCategory } from "./model.ts";
import { byNewest, inspectionView, officialDisclaimer, officialSourceLine, shortDate, violationLine, type OfficialInspection, type OfficialPlaceRecord } from "./official.ts";
import { fdaItemsOf } from "./official-riskfactors.ts";
import { SCALES } from "./official-sources.ts";
import { placeVisits } from "./place-visits.ts";
import { TASK_SEEDS } from "./tasks.ts";
import { printLinesFor, type PrintLine, type PrintPlace } from "./printouts.ts";
import { baselineOrder, quickWinCatalog, quickWinsView, type QuickWinsView, type WinPlace } from "./quickwins.ts";
import { citedPlanId, citedPlanOf, citedPlanView, fixedLine, handoffPlanOf, planFixed, type CitedPlanDef, type CitedPlanDoc, type CitedPlanView, type PlanCtx } from "./citedplan.ts";

// ── What keeps each item right ───────────────────────────────────────────────────────────────────────────────────────

export interface CoverAdd { title: string; how: string; category: TaskCategory; schedule: { perShift?: true; everyDays?: number }; requirementIds: string[] }
export interface CoverDef {
  /** A short plain name for the hand-off line ("cold holding"). Never a judgement. */
  topic: string;
  /** Built-in checks (BASE_CHECKS keys): any one in the place's plan covers it, said as `label`. A list: the first one on is said. */
  base?: { keys: readonly string[]; label: string } | readonly { keys: readonly string[]; label: string }[];
  /** A flow every business has on (the health check-in). */
  builtin?: string;
  /** Checklist item titles that keep it right (the starter checklist's); `add.title` always counts too. */
  titles?: readonly string[];
  /** What "Add it" makes. Absent: nothing to add (a built-in). */
  add?: CoverAdd;
}

const seed = (key: string): CoverAdd => {
  const s = TASK_SEEDS.find((x) => x.key === key)!;
  return { title: s.title, how: s.how, category: s.category, schedule: s.schedule.everyDays ? { everyDays: s.schedule.everyDays } : { perShift: true }, requirementIds: [...s.requirementIds] };
};
/** A checklist item from the AHEAD row: its title, its one line of help, ASSUMED cadence. */
const own = (n: number, category: TaskCategory, schedule: CoverAdd["schedule"], title = AHEAD[n]!.title, requirementIds: string[] = []): CoverAdd => ({ title, how: AHEAD[n]!.help, category, schedule, requirementIds });
const SHIFT = { perShift: true } as const, WEEKLY = { everyDays: 7 } as const;
const CHECKIN = "Health check-in before each shift";

/** Built in one call marked pure, so a bundle that never reads the table (Today's main chunk) drops it. */
function buildCover(): Readonly<Record<number, CoverDef>> {
const DELIVERY = own(11, "opening", SHIFT, "Log each delivery at the door", ["fc-receiving"]);
const COOK = own(18, "opening", SHIFT, "Log cooking and hot-holding temperatures");
const ROOM = own(49, "cleaning", WEEKLY);
return {
  2: { topic: "the manager certificate", add: own(2, "maintenance", { everyDays: 30 }, "Check the manager certificate is on file") },
  3: { topic: "sick workers", builtin: CHECKIN },
  4: { topic: "sick workers", builtin: CHECKIN },
  6: { topic: "eating and drinking", add: own(6, "opening", SHIFT) },
  8: { topic: "handwashing", builtin: CHECKIN },
  9: { topic: "bare hands on ready-to-eat food", add: own(9, "opening", SHIFT) },
  10: { topic: "the hand sink", base: { keys: ["open-restroom", "restroom-4h"], label: "Restroom check at opening: soap, towels, warm water" } },
  11: { topic: "approved suppliers", add: DELIVERY },
  12: { topic: "delivery temperatures", add: DELIVERY },
  13: { topic: "deliveries", add: DELIVERY },
  15: { topic: "raw food stored below ready-to-eat food", add: seed("fridge-order") },
  16: { topic: "sanitizer strength", base: { keys: ["open-sanitizer", "sanitizer-4h"], label: "Sanitizer test at opening" } },
  17: { topic: "food thrown out", add: own(17, "closing", SHIFT) },
  18: { topic: "cooking temperatures", add: COOK },
  19: { topic: "reheating", add: COOK },
  20: { topic: "cooling", base: { keys: ["cooling"], label: "Cooling timer for each batch: 2 and 6 hours" }, add: own(20, "opening", SHIFT, "Start a cooling timer for each batch you cool") },
  21: { topic: "hot holding", add: COOK },
  22: { topic: "cold holding", base: { keys: ["open-cold", "close-cold", "cold-4h"], label: "Fridge and freezer temperatures at opening and closing" } },
  23: { topic: "date marking", add: seed("fifo-dates") },
  24: { topic: "time as a control", add: own(24, "opening", SHIFT) },
  28: { topic: "chemicals", add: seed("sds-firstaid") },
  31: { topic: "ice", add: own(31, "opening", SHIFT) },
  36: { topic: "thermometers", base: { keys: ["open-calibrate"], label: "Thermometer check in ice water at opening" } },
  // Said as what is on (I4): the daily look at opening and closing, else the weekly walk-through (at Essentials only that one is on).
  38: { topic: "pests", base: [{ keys: ["open-pest-look", "close-pest-look"], label: "Pest look at opening and closing" }, { keys: ["pest-walkthrough"], label: "Pest walk-through every week" }] },
  39: { topic: "dry storage", add: own(39, "cleaning", WEEKLY) },
  40: { topic: "hair, rings and clean clothes", add: own(40, "opening", SHIFT) },
  41: { topic: "wiping cloths", base: { keys: ["sanitizer-4h"], label: "Sanitizer and cloth bucket retest every 4 hours" }, add: seed("cloths-laundry") },
  42: { topic: "washing fruits and vegetables", add: own(42, "opening", SHIFT) },
  43: { topic: "in-use utensils", add: own(43, "opening", SHIFT) },
  44: { topic: "utensils and dishes stored", titles: ["Clean and sanitize food-contact surfaces"], add: ROOM },
  45: { topic: "cups, lids and to-go items", add: own(45, "cleaning", WEEKLY) },
  47: { topic: "equipment repair", add: own(47, "maintenance", WEEKLY, "Look over the equipment; report anything broken") },
  48: { topic: "the dish sink or machine", base: { keys: ["open-sanitizer", "sanitizer-4h"], label: "Sanitizer test at opening" } },
  49: { topic: "shelves, handles and equipment outsides", add: ROOM },
  50: { topic: "hot and cold water", add: own(50, "opening", SHIFT, "Log hand sink and dish water temperatures") },
  51: { topic: "the backflow test", add: seed("backflow-test") },
  53: { topic: "the restroom", base: { keys: ["open-restroom", "restroom-4h"], label: "Restroom check at opening" } },
  54: { topic: "garbage", add: seed("close-trash") },
  55: { topic: "floors, walls and ceilings", add: seed("close-floors") },
  56: { topic: "hood filters", add: seed("hood-filters") },
};
}
/** FDA Form 3-A item → what keeps it right. Every AHEAD row has one (test: the two tables agree). */
export const INSPECTION_COVER: Readonly<Record<number, CoverDef>> = /* @__PURE__ */ buildCover();

/**
 * FDA Form 3-A item → the library guide with its rule in full (library.ts topic ids and titles; test: each is a real topic with that
 * title). Every cited item gets a next step (owner, 2026-10-13: "all the violations addressed"): its plan, its check, Add it, this
 * guide, or a library search for the department's words. Items not listed have no one guide that fits; we never guess one.
 */
export const ITEM_GUIDE: Readonly<Record<number, { id: string; title: string }>> = /* @__PURE__ */ buildGuides();
function buildGuides(): Readonly<Record<number, { id: string; title: string }>> {
  const g = (id: string, title: string) => ({ id, title });
  const sick = g("sick-workers", "Sick workers: report it and stay home"), hands = g("handwashing", "Handwashing"), receiving = g("receiving", "Receiving deliveries");
  const holding = g("hot-cold-holding", "Hot and cold holding"), sanitize = g("cleaning-sanitizing", "Cleaning vs sanitizing, and sanitizer strengths");
  return {
    3: sick, 4: sick, 8: hands, 10: hands, 9: g("gloves-bare-hands", "Gloves and no bare hands on ready-to-eat food"),
    11: receiving, 12: receiving, 13: receiving, 15: g("fridge-storage-order", "Fridge and freezer storage order"), 16: sanitize,
    18: g("cooking-temperatures", "Cooking temperatures"), 19: g("reheating", "Reheating leftovers"), 20: g("cooling", "Cooling food in two stages"),
    21: holding, 22: holding, 23: g("date-marking", "Date marking: the 7-day rule"), 24: g("time-as-control", "Time as a public health control (the 4-hour rule)"),
    28: g("chemicals-sds", "Chemicals and Safety Data Sheets (SDS)"), 36: g("thermometers", "Thermometers and calibration"), 38: g("pest-prevention", "Keeping pests out (IPM)"),
    39: g("dry-storage-fifo", "Dry storage and first in, first out (FIFO)"), 40: g("hair-jewelry-nails", "Hair, jewelry, nails and clothes"),
    41: g("wiping-cloths", "Wiping cloths and sanitizer buckets"), 48: g("three-compartment-sink", "The three-compartment sink, step by step"), 53: g("restroom-mops-rags", "Restrooms, mops and rags"),
  };
}
/** The first of a code's FDA items with a guide. */
export const guideOf = (items: readonly number[]) => items.map((n) => ITEM_GUIDE[n]).find((x) => !!x) ?? null;

/** What the place has on: the built-in checks of its plan (levelPlan rule keys) and its checklist items here. */
export interface CoverPlace {
  baseKeys: ReadonlySet<string>;
  tasks: readonly { id: string; title: string; active?: boolean }[];
  /** The place's state, county and permit, for the printouts under each cited item (printouts.ts). Absent: no "Print for this". */
  print?: PrintPlace;
}

export type Coverage =
  | { kind: "covered"; item: number; check: string; open: { label: string; to: string } }
  | { kind: "add"; item: number; title: string; action: { label: string; to: string } }
  | { kind: "none" };

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

/** One FDA item's coverage here, or null when the app has nothing for it. */
export function coverOfItem(n: number, place: CoverPlace): Exclude<Coverage, { kind: "none" }> | null {
  const d = INSPECTION_COVER[n], a = AHEAD[n];
  if (!d || !a) return null;
  const open = { label: "Open", to: a.action.to };
  if (d.builtin) return { kind: "covered", item: n, check: d.builtin, open };
  const bases: readonly { keys: readonly string[]; label: string }[] = !d.base ? [] : Array.isArray(d.base) ? d.base : [d.base as { keys: readonly string[]; label: string }];
  const base = bases.find((b) => b.keys.some((k) => place.baseKeys.has(k)));
  if (base) return { kind: "covered", item: n, check: base.label, open };
  const titles = new Set([...(d.titles ?? []), ...(d.add ? [d.add.title] : [])].map(norm));
  const task = place.tasks.find((t) => t.active !== false && titles.has(norm(t.title)));
  if (task) return { kind: "covered", item: n, check: task.title, open: { label: "Open", to: a.action.to.startsWith("/do") ? a.action.to : "/do" } };
  if (!d.add) return null;
  return { kind: "add", item: n, title: d.add.title, action: a.action };
}

/** A cited code's coverage: the first of its FDA items covered here, else the first we can add, else none (I3). */
export function citedCoverOf(items: readonly number[], place: CoverPlace): Coverage {
  const all = items.map((n) => coverOfItem(n, place)).filter((c): c is NonNullable<typeof c> => !!c);
  return all.find((c) => c.kind === "covered") ?? all.find((c) => c.kind === "add") ?? { kind: "none" };
}

// ── The view ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface InspectionItem {
  code: string;
  /** The department's words for the code, and the inspector's own line when the source gives one. */
  title: string;
  detail: string | null;
  critical: boolean;
  /** Facts from the data ("Corrected during the inspection"). */
  notes: string[];
  cover: Coverage;
  /** "Fix what was cited" (citedplan.ts): the plan for this item. Every cited item has one (owner, 2026-10-14): flies' richer plan,
   *  the item's plan (its situation or general), or the fallback built from the citation's words. Null only in old saved views. */
  plan: CitedPlanView | null;
  /** The library guide with its rule in full (ITEM_GUIDE), when one fits. */
  guide: { id: string; title: string } | null;
  /** "Print for this" (printouts.ts): the sign it requires and the log that shows it kept, each tied to this item by a source. Empty when none is. */
  print: PrintLine[];
  /** The FDA Form 3-A items the department's code is (official-riskfactors.ts fdaItemsOf); [] when no table places it. */
  fda?: number[];
}
/** An item cited at an earlier inspection we hold and NOT at the latest one: a fact, never praise. */
export interface EarlierItem { code: string; title: string; detail: string | null; critical: boolean; dates: string[]; cover: Coverage; guide: { id: string; title: string } | null }
export const EARLIER_TITLE = "Earlier inspections";
export const EARLIER_LINE = "Not cited at your latest inspection.";
export const earlierLede = (n: number) => `${n} item${n === 1 ? "" : "s"} cited at an earlier inspection we hold, not at the latest one.`;

/** Every cited item's one next step (owner: "no cited item is left without a next step"), in this order. */
export type CitedNextStep =
  | { kind: "plan"; label: string }
  | { kind: "covered" | "add"; label: string }
  | { kind: "guide"; label: string; id: string }
  | { kind: "lookup"; label: string; q: string };
export function citedNextStepOf(i: Pick<InspectionItem, "title" | "cover" | "guide"> & { plan?: CitedPlanView | null }): CitedNextStep {
  if (i.plan) return { kind: "plan", label: i.plan.title };
  if (i.cover.kind === "covered") return { kind: "covered", label: coveredWord(i.cover) };
  if (i.cover.kind === "add") return { kind: "add", label: addWord(i.cover) };
  if (i.guide) return { kind: "guide", label: `Read: ${i.guide.title}`, id: i.guide.id };
  return { kind: "lookup", label: "Look it up in the library", q: i.title };
}
export interface RepeatLine { code: string; title: string; line: string; dates: string[] }
export interface CommonLine { item: number; thing: string; cover: Coverage }

export interface LastInspectionView {
  department: string;
  /** The department's page for the latest inspection (or its lookup). */
  recordUrl: string;
  date: string;
  dateText: string;
  type: string | null;
  clean: boolean;
  /** "Your last inspection had nothing cited. Keep it that way." on a clean one. */
  cleanLine: string | null;
  /** When the data doesn't list the items (counts only, or none at all): the department's own record does. */
  listNote: string | null;
  items: InspectionItem[];
  repeats: RepeatLine[];
  /** Items cited at earlier inspections we hold and not at the latest (newest first). Repeats are in `repeats`, never here. */
  earlier: EarlierItem[];
  /** A clean latest inspection: what places like this one are cited for most (commonAhead), or our advice. */
  common: { from: "common" | "advice"; lede: string; items: CommonLine[] } | null;
  /** Distinct "Add it" checks (cited items, or the common ones on a clean inspection). */
  uncovered: number;
  /** Today's one quiet row, or null when nothing is left to add. */
  todayLine: string | null;
  /** A plan's next thing (citedplan.ts), which Today shows before todayLine: the check-back question, or the plan in progress. */
  planToday: { line: string; action: string } | null;
  /** "1 of 4 fixed" here: cited items whose "Fix it today" part is done (citedplan.ts planFixed). Null on a clean inspection. */
  progress: { fixed: number; total: number; line: string } | null;
  /** "Your quick wins" (quickwins.ts), in the baseline order; the API may put the model's cleaned order here instead. */
  wins: QuickWinsView | null;
  /**
   * More points than at the routine inspection before (core place-visits.ts PlaceVisits.setback; owner, 2026-10-17: "we will get through
   * this together"; 2026-10-18: a setback is PRIVATE, said here to the signed-in business and never on the public place page, whose
   * Customer | Business switch is open to every visitor and to Google). The nudge with its numbers, and the calm card. Null otherwise.
   */
  setback: { line: string; head: string; cardLine: string; button: string } | null;
  asOf: string;
  source: string;
  disclaimer: ReturnType<typeof officialDisclaimer>;
}

export const LAST_INSPECTION_TITLE = "Your last inspection";
export const CLEAN_LINE = "Your last inspection had nothing cited. Keep it that way.";
export const NOTHING_YET = "Nothing in the app for this one yet.";
export const ADD_BY_MANAGER = "A manager can add it as a daily check.";
export const NOT_SHOWN_YET = "Not shown yet.";
export const SEE_THEM = "See them";
export const COMMON_LEDE = (area: string) => `What places like yours in ${area} are cited for most:`;
export const ADVICE_LEDE = "Three daily checks that keep food safe (our advice):";
export const coveredWord = (c: Extract<Coverage, { kind: "covered" }>) => `Covered: ${c.check}`;
export const addWord = (c: Extract<Coverage, { kind: "add" }>) => `Add it: ${c.title}`;

/** "2 things from your last inspection aren't daily checks yet". */
export function todayLineOf(n: number): string | null {
  if (n <= 0) return null;
  return `${n} thing${n === 1 ? "" : "s"} from your last inspection ${n === 1 ? "isn't a daily check" : "aren't daily checks"} yet`;
}

/** The same rule with or without the department's critical mark ("4.3.59*" is 4.3.59). */
const codeKey = (code: string) => code.trim().replace(/\*+\s*$/, "").trim().toUpperCase();
/** An inspection whose list of items we hold (a partial list can't say what wasn't cited). */
const listKnown = (i: OfficialInspection) => !i.violations.partial && (i.violations.count === 0 || !!(i.violations.codes?.length || i.violations.items.length));

function lineOf(i: OfficialInspection, code: string, sourceId: string): { title: string; detail: string | null; notes: string[] } {
  const v = i.violations.items.find((x) => x.code === code);
  if (!v) return { title: `Code ${code}`, detail: null, notes: [] };
  const l = violationLine(v, sourceId);
  return { title: l.title, detail: l.detail, notes: l.notes };
}

/** "Cited at 3 of your last 4 inspections", "Cited at both of your last 2 inspections". */
export function repeatLine(k: number, n: number): string {
  const of = k === n ? (n === 2 ? "both" : "all") : String(k);
  return `Cited at ${of} of your last ${n} inspections`;
}

/** Codes cited at more than one inspection whose list we hold (I5), most often first, then the newest. */
export function repeatsOf(r: OfficialPlaceRecord): RepeatLine[] {
  const known = [...r.inspections].sort(byNewest).filter(listKnown);
  const seen = new Map<string, { code: string; title: string; dates: string[] }>();
  for (const i of known) {
    for (const c of new Set(citedOf(i).map((x) => x.code))) {
      const k = codeKey(c);
      const e = seen.get(k);
      if (e) { if (!e.dates.includes(i.date)) e.dates.push(i.date); continue; }
      seen.set(k, { code: c, title: lineOf(i, c, r.place.sourceId).title, dates: [i.date] });
    }
  }
  return [...seen.values()].filter((e) => e.dates.length > 1)
    .sort((a, b) => b.dates.length - a.dates.length || b.dates[0]!.localeCompare(a.dates[0]!))
    .map((e) => ({ code: e.code, title: e.title, line: repeatLine(e.dates.length, known.length), dates: e.dates.map(shortDate) }));
}

/** The page's view of one linked record. Null when the department lists no inspection yet. */
export function lastInspectionView(r: OfficialPlaceRecord, place: CoverPlace, o: { common: readonly number[] | null; /** The plans' taps here (citedplan.ts), and whose location. */ plans?: { locationId: string; docs: readonly CitedPlanDoc[]; today: string }; /** The place, for the printouts that apply here. */ win?: WinPlace }): LastInspectionView | null {
  const last = [...r.inspections].sort(byNewest)[0];
  if (!last) return null;
  const src = r.place.sourceId;
  const clean = isClean({ ...r, inspections: [last] });
  const cited = new Map<string, ReturnType<typeof citedOf>[number]>();
  for (const c of citedOf(last)) if (!cited.has(c.code)) cited.set(c.code, c);
  // Repeats stay at the top for what the latest inspection cited; a code cited before and not now is under `earlier`, with its dates
  // (owner, 2026-10-13: "a repeat isn't listed twice").
  const nowKeys = new Set(citedOf(last).map((c) => codeKey(c.code)));
  const repeats = repeatsOf(r).filter((x) => nowKeys.has(codeKey(x.code)));
  const repeated = new Set(repeats.map((x) => codeKey(x.code)));
  const at = { state: r.place.jurisdiction.split("/")[0] || null, county: r.place.area.replace(/ County$/, "") || null, sourceId: src };
  const items: InspectionItem[] = [...cited.values()].map((c) => {
    const line = lineOf(last, c.code, src), fda = fdaItemsOf(src, c.code);
    const def = planDefFor(fda, line);
    const watchTitles = new Set([def.watch.add.title, ...(def.watch.titles ?? [])].map(norm));
    const plan = citedPlanView(def, {
      code: c.code, citedOn: last.date, repeat: repeated.has(codeKey(c.code)), place: at, watchOn: [...place.baseKeys],
      doc: o.plans?.docs.find((d) => d.id === citedPlanId(o.plans!.locationId, def.id, last.date, c.code)) ?? null,
      dailyTask: place.tasks.some((t) => t.active !== false && watchTitles.has(norm(t.title))), today: o.plans?.today ?? new Date().toISOString().slice(0, 10),
    });
    return { code: c.code, ...line, critical: c.critical, cover: citedCoverOf(fda, place), plan, guide: guideOf(fda), fda: [...fda], print: printLinesFor(fda, place.print) };
  });
  items.sort((a, b) => Number(b.critical) - Number(a.critical));
  const view = inspectionView(last, src);
  const listNote = last.violations.partial ? "The data does not list this inspection's items. The department's record lists them." : view.listNote;
  let common: LastInspectionView["common"] = null;
  if (clean) {
    const from = o.common?.length ? "common" as const : "advice" as const;
    const pick = [...new Set((from === "common" ? o.common! : AHEAD_DEFAULT).filter((n) => !!INSPECTION_COVER[n]))].slice(0, AHEAD_MAX);
    common = { from, lede: from === "common" ? COMMON_LEDE(r.place.area) : ADVICE_LEDE, items: pick.map((n) => ({ item: n, thing: AHEAD[n]!.thing, cover: citedCoverOf([n], place) })) };
  }
  const earlier = earlierOf(r, last, place);
  const adds = new Set([...items.map((i) => i.cover), ...(common?.items.map((c) => c.cover) ?? [])].filter((c) => c.kind === "add").map((c) => (c as Extract<Coverage, { kind: "add" }>).title));
  const winPlace: WinPlace = { ...(at.state ? { state: at.state as WinPlace["state"] } : {}), ...o.win };
  const scale = SCALES[last.scaleId];
  const sb = scale ? placeVisits(r.inspections, scale, { name: r.place.name, by: r.place.department, today: new Date(o.plans?.today ?? new Date().toISOString().slice(0, 10)) }).setback : null;
  return {
    wins: quickWinsView(baselineOrder(quickWinCatalog({ items, repeats }, winPlace))),
    setback: sb ? { line: sb.line, head: sb.head, cardLine: sb.cardLine, button: sb.button } : null,
    department: r.place.department, recordUrl: last.recordUrl || r.place.recordUrl, date: last.date, dateText: shortDate(last.date), type: last.type,
    clean, cleanLine: clean ? CLEAN_LINE : null, listNote, items, repeats, earlier, common,
    uncovered: adds.size, todayLine: todayLineOf(adds.size), planToday: (items.find((i) => i.plan?.recheck.ask)?.plan ?? items.find((i) => i.plan?.today)?.plan)?.today ?? null,
    progress: progressOf(items),
    asOf: `As of ${shortDate(last.date)}, from ${r.place.department}.`, source: officialSourceLine(r.place.department),
    disclaimer: officialDisclaimer(r.place.area, last.recordUrl || r.place.recordUrl, r.place.department),
  };
}

/** The context an item plan reads: what keeps the item right here (INSPECTION_COVER) and its library guide (ITEM_GUIDE). */
export const planCtxOf = (n: number): PlanCtx => ({ ...(INSPECTION_COVER[n] ? { cover: INSPECTION_COVER[n] } : {}), guide: ITEM_GUIDE[n] ?? null });
/** The plan for a cited code's FDA items and the department's words (citedplan.ts citedPlanOf): never null. The API reads it too. */
export const planDefFor = (fda: readonly number[], words: { title: string; detail: string | null }): CitedPlanDef => citedPlanOf(fda, words, planCtxOf);

/** "1 of 4 fixed" over the cited items (each has a plan). Null when nothing was cited. */
export function progressOf(items: readonly Pick<InspectionItem, "plan">[]): LastInspectionView["progress"] {
  const planned = items.filter((i) => i.plan);
  if (!planned.length) return null;
  const fixed = planned.filter((i) => planFixed(i.plan!)).length;
  return { fixed, total: planned.length, line: fixedLine(fixed, planned.length) };
}

/** A business's places, each "Main St: 3 of 5 fixed", joined: "Main St: 3 of 5 fixed · Downtown: 1 of 4". Only places with something cited. */
export function rollupLine(rows: readonly { name: string; fixed: number; total: number }[]): string | null {
  const xs = rows.filter((r) => r.total > 0);
  if (!xs.length) return null;
  return xs.map((r, i) => `${r.name}: ${r.fixed} of ${r.total}${i === 0 ? " fixed" : ""}`).join(" · ");
}

/** Codes cited at earlier inspections whose list we hold (I5) and not at the latest; the words from the newest that cited it. */
function earlierOf(r: OfficialPlaceRecord, last: OfficialInspection, place: CoverPlace): EarlierItem[] {
  const now = new Set(citedOf(last).map((c) => codeKey(c.code)));
  const seen = new Map<string, EarlierItem>();
  for (const i of [...r.inspections].sort(byNewest).filter((x) => x !== last && listKnown(x))) {
    for (const c of citedOf(i)) {
      const k = codeKey(c.code);
      if (now.has(k)) continue;
      const e = seen.get(k);
      if (e) { if (!e.dates.includes(shortDate(i.date))) e.dates.push(shortDate(i.date)); e.critical ||= c.critical; continue; }
      const l = lineOf(i, c.code, r.place.sourceId), fda = fdaItemsOf(r.place.sourceId, c.code);
      seen.set(k, { code: c.code, title: l.title, detail: l.detail, critical: c.critical, dates: [shortDate(i.date)], cover: citedCoverOf(fda, place), guide: guideOf(fda) });
    }
  }
  return [...seen.values()];
}

/**
 * GET /orgs/:orgId/locations/:id/last-inspection: the linked record, shown (the department's panel and our view), or one line
 * (held for display approval: "Not shown yet."; the area not showing now; the record gone). Null when nothing is linked.
 */
export type LastInspectionAnswer =
  | { status: "shown"; linked: Extract<import("./locationlink.ts").LinkedRecord, { status: "shown" }>; view: LastInspectionView | null }
  | { status: "held" | "unavailable" | "gone"; line: string };
export interface LastInspectionResponse { inspection: LastInspectionAnswer | null; /** A manager or above: "Add it" makes the checklist item. */ canAdd: boolean; /** The business across its places, when more than one has something cited: "Main St: 3 of 5 fixed · Downtown: 1 of 4" (rollupLine). */ rollup?: string | null }

// ── The prep hand-off preview's block ────────────────────────────────────────────────────────────────────────────────

export interface HandoffInspection {
  line: string; checksLead: string | null; checks: string[]; notYet: string | null;
  /** A cited item's plan (citedplan.ts), read-only here: the owner taps it in the app. */
  plan?: { lede: string; steps: string[]; then: string } | null;
  /** EVERY item the latest inspection cited, each with its next step (owner, 2026-10-13), and the earlier ones not cited since. */
  items?: HandoffItem[];
  earlier?: HandoffItem[];
}
/** One cited item in the preview: the department's words and code, our one next step, and a link a person without an account can open. */
export interface HandoffItem { code: string; title: string; detail: string | null; dates?: string[]; next: string; link: { label: string; url: string } | null }
/** The public library (apps/landing build-library.ts: /library/<id>/), for the preview's prospect, who has no account yet. */
export const LIBRARY_ORIGIN = "https://cleancleaver.com";

const andList = (xs: readonly string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

/**
 * "Your last inspection (Jun 3, 2026) cited cold holding and the hand sink. Both are now daily checks: …". A clean one: "…had
 * nothing cited. We set up the checks places like yours get cited for most." Only what is true now: a check is named only when
 * it covers the item here (I4); an item not covered is said as "Not a daily check yet".
 */
export function handoffInspectionOf(v: LastInspectionView): HandoffInspection {
  const record = { label: "See the department's record", url: v.recordUrl };
  const itemOf = (i: Pick<InspectionItem, "code" | "title" | "detail" | "cover" | "guide"> & { plan?: CitedPlanView | null; dates?: string[] }, earlier = false): HandoffItem => {
    // The preview names the check that keeps an item right where there is one; the plan where nothing else answers it (and flies').
    const n = citedNextStepOf(i.plan && (!i.plan.kind || i.cover.kind === "none") ? i : { ...i, plan: null });
    const next = n.kind === "plan" ? (n.label.includes(":") ? `${n.label}, ready in the app` : `${n.label}: ready in the app`) : n.kind === "lookup" ? NOTHING_YET : n.label;
    const link = i.guide ? { label: i.guide.title, url: `${LIBRARY_ORIGIN}/library/${i.guide.id}/` } : n.kind === "lookup" || n.kind === "plan" ? record : null;
    return { code: i.code, title: i.title, detail: i.detail, ...(i.dates ? { dates: i.dates } : {}), next: earlier ? `${EARLIER_LINE} ${next}` : next, link };
  };
  const lists = { ...(v.items.length ? { items: v.items.map((i) => itemOf(i)) } : {}), ...(v.earlier.length ? { earlier: v.earlier.map((i) => itemOf(i, true)) } : {}) };
  if (v.clean) {
    const on = [...new Set((v.common?.items ?? []).map((c) => c.cover).filter((c) => c.kind === "covered").map((c) => (c as Extract<Coverage, { kind: "covered" }>).check))];
    if (!on.length) return { line: "Your last inspection had nothing cited.", checksLead: null, checks: [], notYet: null, ...lists };
    const lead = v.common?.from === "common" ? "We set up the checks places like yours get cited for most." : "These daily checks keep it that way.";
    return { line: `Your last inspection had nothing cited. ${lead}`, checksLead: null, checks: on, notYet: null, ...lists };
  }
  const mapped = v.items.filter((i) => i.cover.kind !== "none");
  const topicOf = (i: InspectionItem) => INSPECTION_COVER[(i.cover as Exclude<Coverage, { kind: "none" }>).item]!.topic;
  const topics = [...new Set(mapped.map(topicOf))];
  const rest = v.items.length - mapped.length;
  const what = topics.length ? andList([...topics, ...(rest ? [`${rest} more item${rest === 1 ? "" : "s"}`] : [])]) : `${v.items.length} item${v.items.length === 1 ? "" : "s"}`;
  const line = v.items.length ? `Your last inspection (${v.dateText}) cited ${what}.` : `Your last inspection (${v.dateText}).`;
  const coveredTopics = [...new Set(mapped.filter((i) => i.cover.kind === "covered").map(topicOf))];
  const checks = [...new Set(mapped.map((i) => i.cover).filter((c) => c.kind === "covered").map((c) => (c as Extract<Coverage, { kind: "covered" }>).check))];
  const open = topics.filter((t) => !coveredTopics.includes(t));
  const all = checks.length && !open.length;
  const checksLead = !checks.length ? null
    : all ? (coveredTopics.length === 1 ? "It's now a daily check:" : coveredTopics.length === 2 ? "Both are now daily checks:" : `All ${coveredTopics.length} are now daily checks:`)
    : "Now daily checks:";
  // The preview names one plan, the first cited item's (the critical ones come first). The rest are in the app.
  const planned = v.items.find((i) => i.plan);
  const def = planned ? planDefFor(planned.fda ?? [], { title: planned.title, detail: planned.detail }) : null;
  return { line, checksLead, checks, notYet: open.length ? `Not a daily check yet: ${andList(open)}.` : null, ...(def ? { plan: handoffPlanOf(def) } : {}), ...lists };
}

// ── The binder's page ────────────────────────────────────────────────────────────────────────────────────────────────

/** A plan's trail goes in the binder once it is started (the flies plan's, as shipped, always). */
const trailed = (i: Pick<InspectionItem, "plan">) => !!i.plan && (i.plan.planId === "flies" || i.plan.trail.length > 1);

/** "Inspection follow-up": each cited item in the department's words and the daily check that keeps it right, for the inspector. */
export function inspectionFollowUpSection(v: LastInspectionView): BinderSection {
  const cell = (c: Coverage) => (c.kind === "covered" ? c.check : c.kind === "add" ? "Not a daily check yet" : NOTHING_YET.replace(/\.$/, ""));
  const rows = [
    ...v.items.map((i) => ({ cells: [`${i.critical ? "Critical. " : ""}${i.title}${i.detail ? ` ${i.detail}` : ""}`, i.code, cell(i.cover)], ...(i.cover.kind === "covered" ? { tone: "ok" as const } : {}) })),
    // Earlier inspections (owner, 2026-10-13): items cited before and not at the latest one, with their dates. Facts only.
    ...v.earlier.map((i) => ({ cells: [`Earlier (${i.dates.join(", ")}). ${i.critical ? "Critical. " : ""}${i.title}${i.detail ? ` ${i.detail}` : ""} ${EARLIER_LINE}`, i.code, cell(i.cover)], ...(i.cover.kind === "covered" ? { tone: "ok" as const } : {}) })),
  ];
  return {
    id: "inspection-follow-up",
    heading: "Inspection follow-up",
    intro: `${v.department}, inspection on ${v.dateText}. Each item in the department's words and code, with the daily check here that keeps it right${v.earlier.length ? ", then items cited at earlier inspections and not at this one" : ""}. ${v.source}`,
    ...(v.clean ? { lines: [CLEAN_LINE] } : { empty: v.listNote ?? "No items listed.", ...(v.items.some((i) => trailed(i)) ? { lines: v.items.flatMap((i) => (trailed(i) ? i.plan!.trail : [])) } : {}) }),
    ...(rows.length ? { table: { columns: ["Cited", "Code", "Daily check here"], widths: [3.4, 0.9, 2.7], rows } } : {}),
  };
}
